import { FormEvent, useCallback, useEffect, useState } from "react";

import type { ProductContent, ProductFormation, ProductPresentationVideo } from "@itaas/pep-contracts";

import { getProducts } from "../api/pep-api";

import {

  addProductFormation,

  deleteProductFormation,

  fetchProductContent,

  reorderProductFormations,

  saveProductContent,

  updateProductFormation,

  uploadProductMedia,

} from "./product-content-api";



type VideoSource = ProductPresentationVideo["source"];



export function AdminProductContentPage() {

  const [products, setProducts] = useState<{ slug: string; name: string }[]>([]);

  const [slug, setSlug] = useState("edu");

  const [content, setContent] = useState<ProductContent | null>(null);

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);



  const [videoTitle, setVideoTitle] = useState("Présentation produit");

  const [videoUrl, setVideoUrl] = useState("");

  const [videoSource, setVideoSource] = useState<VideoSource>("youtube");

  const [videoPublished, setVideoPublished] = useState(false);



  const [newFormationTitle, setNewFormationTitle] = useState("");

  const [newFormationDesc, setNewFormationDesc] = useState("");

  const [newFormationDuration, setNewFormationDuration] = useState("");



  const load = useCallback(async (productSlug: string) => {

    setLoading(true);

    setError(null);

    try {

      const data = await fetchProductContent(productSlug);

      setContent(data);

      setVideoTitle(data.presentationVideo?.title ?? "Présentation produit");

      setVideoUrl(data.presentationVideo?.url ?? "");

      setVideoSource(data.presentationVideo?.source ?? "youtube");

      setVideoPublished(data.presentationVideo?.published ?? false);

    } catch {

      setError("Impossible de charger le contenu produit.");

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    getProducts()

      .then((list) =>

        setProducts(

          list.map((p) => ({

            slug: p.slug,

            name: p.displayNameFr ?? p.displayName,

          })),

        ),

      )

      .catch(console.error);

  }, []);



  useEffect(() => {

    void load(slug);

  }, [slug, load]);



  async function saveVideo(e: FormEvent) {

    e.preventDefault();

    setMessage(null);

    setError(null);

    try {

      const data = await saveProductContent(slug, {

        presentationVideo: {

          title: videoTitle,

          url: videoUrl,

          source: videoSource,

          published: videoPublished,

        },

      });

      setContent(data);

      setMessage("Vidéo de présentation enregistrée.");

    } catch {

      setError("Échec enregistrement vidéo.");

    }

  }



  async function onUploadPresentation(file: File) {

    setMessage(null);

    setError(null);

    try {

      const data = await uploadProductMedia(slug, file, {

        target: "presentation",

        title: videoTitle,

      });

      setContent(data);

      setVideoUrl(data.presentationVideo?.url ?? "");

      setVideoSource("file");

      setMessage(`Fichier uploadé : ${data.presentationVideo?.url}`);

    } catch {

      setError("Upload vidéo échoué.");

    }

  }



  async function onAddFormation(e: FormEvent) {

    e.preventDefault();

    if (!newFormationTitle.trim()) return;

    setMessage(null);

    setError(null);

    try {

      const data = await addProductFormation(slug, {

        title: newFormationTitle.trim(),

        description: newFormationDesc.trim() || undefined,

        durationMin: newFormationDuration ? Number(newFormationDuration) : undefined,

      });

      setContent(data);

      setNewFormationTitle("");

      setNewFormationDesc("");

      setNewFormationDuration("");

      setMessage("Formation ajoutée.");

    } catch {

      setError("Impossible d'ajouter la formation.");

    }

  }



  async function togglePublished(id: string, published: boolean) {

    try {

      const data = await updateProductFormation(slug, id, { published });

      setContent(data);

    } catch {

      setError("Impossible de mettre à jour la publication.");

    }

  }



  async function onDeleteFormation(id: string) {

    if (!confirm("Supprimer cette formation ?")) return;

    setMessage(null);

    setError(null);

    try {

      const data = await deleteProductFormation(slug, id);

      setContent(data);

      setMessage("Formation supprimée.");

    } catch {

      setError("Suppression échouée.");

    }

  }



  async function onUploadFormation(file: File, formationId: string) {

    setMessage(null);

    setError(null);

    try {

      const data = await uploadProductMedia(slug, file, {

        target: "formation",

        formationId,

      });

      setContent(data);

      setMessage("Vidéo formation uploadée.");

    } catch {

      setError("Upload formation échoué.");

    }

  }



  async function moveFormation(id: string, direction: "up" | "down") {

    if (!content) return;

    const sorted = content.formations.slice().sort((a, b) => a.sortOrder - b.sortOrder);

    const idx = sorted.findIndex((f) => f.id === id);

    if (idx < 0) return;

    const swap = direction === "up" ? idx - 1 : idx + 1;

    if (swap < 0 || swap >= sorted.length) return;

    const orderedIds = sorted.map((f) => f.id);

    [orderedIds[idx], orderedIds[swap]] = [orderedIds[swap], orderedIds[idx]];

    try {

      const data = await reorderProductFormations(slug, orderedIds);

      setContent(data);

    } catch {

      setError("Réordonnancement échoué.");

    }

  }



  const sortedFormations = content?.formations

    .slice()

    .sort((a, b) => a.sortOrder - b.sortOrder) ?? [];



  return (

    <div>

      <h1 className="h3 fw-bold mb-2">Contenu produit — vidéos & formations</h1>

      <p className="text-secondary mb-4">

        Gérez la vidéo de présentation et les formations affichées sur{" "}

        <code>/products/:slug</code>.

      </p>



      <div className="row g-4">

        <div className="col-lg-3">

          <div className="card border-0 shadow-sm">

            <div className="card-body">

              <label className="form-label fw-semibold">Produit</label>

              <select

                className="form-select"

                value={slug}

                onChange={(e) => setSlug(e.target.value)}

              >

                {products.map((p) => (

                  <option key={p.slug} value={p.slug}>

                    {p.name}

                  </option>

                ))}

              </select>

              <a

                className="btn btn-sm btn-outline-primary mt-3 w-100"

                href={`/products/${slug}`}

                target="_blank"

                rel="noreferrer"

              >

                Voir la fiche →

              </a>

            </div>

          </div>

        </div>



        <div className="col-lg-9">

          {loading && <p className="text-secondary">Chargement…</p>}

          {message && <div className="alert alert-success py-2">{message}</div>}

          {error && <div className="alert alert-danger py-2">{error}</div>}



          {!loading && content && (

            <>

              <div className="card border-0 shadow-sm mb-4">

                <div className="card-body">

                  <h2 className="h5 fw-bold mb-3">Vidéo de présentation</h2>

                  <form onSubmit={saveVideo} className="row g-3">

                    <div className="col-md-6">

                      <label className="form-label">Titre</label>

                      <input

                        className="form-control"

                        value={videoTitle}

                        onChange={(e) => setVideoTitle(e.target.value)}

                      />

                    </div>

                    <div className="col-md-6">

                      <label className="form-label">Source</label>

                      <select

                        className="form-select"

                        value={videoSource}

                        onChange={(e) => setVideoSource(e.target.value as VideoSource)}

                      >

                        <option value="youtube">YouTube</option>

                        <option value="vimeo">Vimeo</option>

                        <option value="file">Fichier uploadé</option>

                      </select>

                    </div>

                    <div className="col-12">

                      <label className="form-label">URL (YouTube, Vimeo ou /assets/…)</label>

                      <input

                        className="form-control font-monospace small"

                        value={videoUrl}

                        onChange={(e) => setVideoUrl(e.target.value)}

                        placeholder="https://youtube.com/watch?v=… ou https://vimeo.com/…"

                      />

                    </div>

                    <div className="col-12">

                      <label className="form-label">Uploader un fichier MP4 / WebM</label>

                      <input

                        type="file"

                        className="form-control"

                        accept="video/mp4,video/webm"

                        onChange={(e) => {

                          const f = e.target.files?.[0];

                          if (f) void onUploadPresentation(f);

                        }}

                      />

                    </div>

                    <div className="col-12">

                      <div className="form-check">

                        <input

                          id="video-pub"

                          type="checkbox"

                          className="form-check-input"

                          checked={videoPublished}

                          onChange={(e) => setVideoPublished(e.target.checked)}

                        />

                        <label htmlFor="video-pub" className="form-check-label">

                          Publier sur la fiche produit

                        </label>

                      </div>

                    </div>

                    <div className="col-12">

                      <button type="submit" className="btn btn-primary">

                        Enregistrer la présentation

                      </button>

                    </div>

                  </form>

                </div>

              </div>



              <div className="card border-0 shadow-sm mb-4">

                <div className="card-body">

                  <h2 className="h5 fw-bold mb-3">Formations</h2>

                  <form

                    onSubmit={onAddFormation}

                    className="row g-2 align-items-end mb-4 p-3 bg-body-tertiary rounded-3"

                  >

                    <div className="col-md-4">

                      <label className="form-label small">Nom de la formation</label>

                      <input

                        className="form-control form-control-sm"

                        value={newFormationTitle}

                        onChange={(e) => setNewFormationTitle(e.target.value)}

                        placeholder="Ex. Inscription élève — wizard"

                        required

                      />

                    </div>

                    <div className="col-md-4">

                      <label className="form-label small">Description</label>

                      <input

                        className="form-control form-control-sm"

                        value={newFormationDesc}

                        onChange={(e) => setNewFormationDesc(e.target.value)}

                      />

                    </div>

                    <div className="col-md-2">

                      <label className="form-label small">Durée (min)</label>

                      <input

                        type="number"

                        min={1}

                        className="form-control form-control-sm"

                        value={newFormationDuration}

                        onChange={(e) => setNewFormationDuration(e.target.value)}

                      />

                    </div>

                    <div className="col-md-2">

                      <button type="submit" className="btn btn-sm btn-primary w-100">

                        + Ajouter

                      </button>

                    </div>

                  </form>



                  {sortedFormations.length === 0 && (

                    <p className="text-secondary small mb-0">

                      Aucune formation — ajoutez-en une ci-dessus.

                    </p>

                  )}



                  <div className="d-flex flex-column gap-3">

                    {sortedFormations.map((f, index) => (

                      <FormationRow

                        key={f.id}

                        formation={f}

                        canMoveUp={index > 0}

                        canMoveDown={index < sortedFormations.length - 1}

                        onMoveUp={() => moveFormation(f.id, "up")}

                        onMoveDown={() => moveFormation(f.id, "down")}

                        onSave={(patch) =>

                          updateProductFormation(slug, f.id, patch).then(setContent)

                        }

                        onTogglePublished={(published) => togglePublished(f.id, published)}

                        onDelete={() => onDeleteFormation(f.id)}

                        onUpload={(file) => onUploadFormation(file, f.id)}

                      />

                    ))}

                  </div>

                </div>

              </div>

            </>

          )}

        </div>

      </div>

    </div>

  );

}



type FormationDraft = {

  title: string;

  description: string;

  durationMin: string;

  videoUrl: string;

};



function FormationRow({

  formation,

  canMoveUp,

  canMoveDown,

  onMoveUp,

  onMoveDown,

  onSave,

  onTogglePublished,

  onDelete,

  onUpload,

}: {

  formation: ProductFormation;

  canMoveUp: boolean;

  canMoveDown: boolean;

  onMoveUp: () => void;

  onMoveDown: () => void;

  onSave: (patch: Partial<ProductFormation>) => Promise<void>;

  onTogglePublished: (published: boolean) => void;

  onDelete: () => void;

  onUpload: (file: File) => void;

}) {

  const [draft, setDraft] = useState<FormationDraft>({

    title: formation.title,

    description: formation.description ?? "",

    durationMin: formation.durationMin != null ? String(formation.durationMin) : "",

    videoUrl: formation.videoUrl ?? "",

  });

  const [saving, setSaving] = useState(false);



  useEffect(() => {

    setDraft({

      title: formation.title,

      description: formation.description ?? "",

      durationMin: formation.durationMin != null ? String(formation.durationMin) : "",

      videoUrl: formation.videoUrl ?? "",

    });

  }, [formation]);



  const dirty =

    draft.title !== formation.title ||

    draft.description !== (formation.description ?? "") ||

    draft.durationMin !==

      (formation.durationMin != null ? String(formation.durationMin) : "") ||

    draft.videoUrl !== (formation.videoUrl ?? "");



  async function handleSave() {

    setSaving(true);

    try {

      await onSave({

        title: draft.title.trim(),

        description: draft.description.trim() || undefined,

        durationMin: draft.durationMin ? Number(draft.durationMin) : undefined,

        videoUrl: draft.videoUrl.trim() || undefined,

      });

    } finally {

      setSaving(false);

    }

  }



  return (

    <div className="border rounded-3 p-3">

      <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">

        <div className="d-flex gap-2 align-items-center flex-grow-1">

          <div className="btn-group btn-group-sm">

            <button

              type="button"

              className="btn btn-outline-secondary"

              disabled={!canMoveUp}

              onClick={onMoveUp}

              title="Monter"

            >

              ↑

            </button>

            <button

              type="button"

              className="btn btn-outline-secondary"

              disabled={!canMoveDown}

              onClick={onMoveDown}

              title="Descendre"

            >

              ↓

            </button>

          </div>

          <input

            className="form-control form-control-sm fw-semibold"

            value={draft.title}

            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}

          />

        </div>

        <div className="d-flex gap-2 align-items-center">

          <div className="form-check form-switch mb-0">

            <input

              className="form-check-input"

              type="checkbox"

              checked={formation.published}

              onChange={(e) => onTogglePublished(e.target.checked)}

              id={`pub-${formation.id}`}

            />

            <label className="form-check-label small" htmlFor={`pub-${formation.id}`}>

              Publié

            </label>

          </div>

          <button type="button" className="btn btn-sm btn-outline-danger" onClick={onDelete}>

            Supprimer

          </button>

        </div>

      </div>

      <textarea

        className="form-control form-control-sm mb-2"

        rows={2}

        placeholder="Description"

        value={draft.description}

        onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}

      />

      <div className="row g-2 align-items-center">

        <div className="col-md-2">

          <input

            type="number"

            className="form-control form-control-sm"

            placeholder="Min"

            value={draft.durationMin}

            onChange={(e) => setDraft((d) => ({ ...d, durationMin: e.target.value }))}

          />

        </div>

        <div className="col-md-5">

          <input

            className="form-control form-control-sm font-monospace"

            placeholder="URL vidéo (YouTube, Vimeo, /assets/)"

            value={draft.videoUrl}

            onChange={(e) => setDraft((d) => ({ ...d, videoUrl: e.target.value }))}

          />

        </div>

        <div className="col-md-3">

          <input

            type="file"

            className="form-control form-control-sm"

            accept="video/mp4,video/webm"

            onChange={(e) => {

              const file = e.target.files?.[0];

              if (file) onUpload(file);

            }}

          />

        </div>

        <div className="col-md-2">

          <button

            type="button"

            className="btn btn-sm btn-primary w-100"

            disabled={!dirty || saving}

            onClick={() => void handleSave()}

          >

            {saving ? "…" : "Enregistrer"}

          </button>

        </div>

      </div>

      {dirty && (

        <p className="small text-warning mb-0 mt-2">Modifications non enregistrées.</p>

      )}

    </div>

  );

}

